import { useState, useRef, useEffect } from "react";
import { CgProfile } from "react-icons/cg";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import E_SearchBar from "./E_SearchBar";
import axios from "axios";
import { MdOutlineLogout } from "react-icons/md";
import { IoPersonOutline } from "react-icons/io5";
import { IoReorderThreeOutline } from "react-icons/io5";
import { useUser } from "../../context/UserContext";
import { RiNotification2Line } from "react-icons/ri";
import { format } from "date-fns";

function E_NavBar() {
  const navigate = useNavigate();
  const [showNotification, setShowNotification] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showRequests, setShowRequests] = useState(false);
  const [notifications] = useState([]);
  const [scrolled, setScrolled] = useState(false);
  const baseUrl = process.env.REACT_APP_API_URL;

  const notifRef = useRef();
  const profileRef = useRef();
  const requestRef = useRef();
  const { currentUser, setCurrentUser, loading } = useUser();

  const handleLogout = () => {
    localStorage.removeItem("token");
    setCurrentUser(null);
    navigate("/login");
  };

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotification(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfile(false);
      }
      if (requestRef.current && !requestRef.current.contains(event.target)) {
        setShowRequests(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Animation variants
  const dropdownVariants = {
    hidden: { 
      opacity: 0, 
      y: -10,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    },
    exit: { 
      opacity: 0, 
      y: -10,
      scale: 0.95,
      transition: {
        duration: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.05,
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    })
  };

  if (loading) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="sticky top-0 h-20 flex items-center justify-between px-4 sm:px-8 relative z-50 bg-white/80 backdrop-blur-md border-b border-slate-200"
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-32 bg-gradient-to-r from-slate-200 to-slate-300 rounded-lg animate-pulse"></div>
        </div>
        <div className="flex items-center gap-5">
          <div className="hidden sm:block">
            <div className="h-10 w-64 bg-gradient-to-r from-slate-200 to-slate-300 rounded-full animate-pulse"></div>
          </div>
          <div className="h-10 w-10 bg-gradient-to-r from-slate-200 to-slate-300 rounded-full animate-pulse"></div>
          <div className="h-10 w-10 bg-gradient-to-r from-slate-200 to-slate-300 rounded-full animate-pulse"></div>
        </div>
      </motion.div>
    );
  }

  if (!currentUser) {
    return null;
  }

  return (
    <div className="top-0 h-20 flex items-center justify-between px-4 sm:px-8 z-[999] ">
      {/* Left: Hamburger + Logo */}
      <div className="flex items-center gap-2 -ml-4">
        <button
          className="hidden text-slate-700 p-2 rounded-xl hover:bg-slate-100 transition-colors"
        >
          <IoReorderThreeOutline className="text-4xl" />
        </button>

        <img
          src="/companylogo.png"
          alt="Company Logo"
          className="h-16 w-auto cursor-pointer"
        />
      </div>

      {/* Right: Search, Notifications, Profile */}
      <div className="flex items-center gap-2 relative">
        {/* Search - desktop only 
        <div className="hidden sm:block">
          <E_SearchBar />
        </div>
         */}

        {/* Notifications 
        <div ref={notifRef} className="relative">
          <button
            onClick={() => setShowNotification(!showNotification)}
            aria-label="Notifications"
            className="relative p-2.5 rounded-xl text-slate-700 hover:bg-slate-100 transition-colors group"
          >
            <RiNotification2Line className="text-3xl group-hover:text-slate-900 transition-colors" />
            {notifications.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-gradient-to-br from-red-500 to-red-600 text-white text-[10px] min-w-[18px] h-[18px] flex items-center justify-center rounded-full font-semibold shadow-lg">
                {notifications.length}
              </span>
            )}
          </button>
          
          {showNotification && (
            <div className="absolute right-0 mt-3 w-80 bg-white border border-slate-200 shadow-2xl rounded-2xl overflow-hidden z-50 animate-in fade-in zoom-in duration-150">
                <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-5 py-4 border-b border-slate-200">
                  <h2 className="font-semibold text-slate-800 text-lg">Notifications</h2>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="px-5 py-12 text-center">
                      <RiNotification2Line className="text-5xl text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-400 text-sm">No new notifications</p>
                    </div>
                  ) : (
                    <ul className="py-2">
                      {notifications.map((note, i) => (
                        <li
                          key={i}
                          className="px-5 py-3 hover:bg-slate-50 transition-colors cursor-pointer border-b border-slate-100 last:border-0"
                        >
                          <p className="text-sm text-slate-700">{note}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
            </div>
          )}
        </div>
         */}
        {/* Profile dropdown */}
        <div ref={profileRef} className="relative">
<button
  onClick={() => setShowProfile(!showProfile)}
  aria-label="Profile"
  className="p-1 rounded-xl hover:bg-slate-100 transition-colors group"
>
  {currentUser?.profilePictureUrl ? (
    <img
      src={currentUser.profilePictureUrl}
      alt="Profile"
      className="w-11 h-11 rounded-full object-cover border border-slate-200 shadow-sm"
    />
  ) : (
    <div className="p-1.5 rounded-full text-slate-700">
      <CgProfile className="text-3xl group-hover:text-slate-900 transition-colors" />
    </div>
  )}
</button>


          
          {showProfile && (
            <div className="absolute right-0 mt-3 w-56 bg-white border border-slate-200 shadow-2xl rounded-2xl overflow-hidden z-[100] animate-in fade-in zoom-in duration-150">
              <div className="p-2">
                <button
                  onClick={() => {
                    setShowProfile(false);
                    navigate("/employee/profile");
                  }}
                  className="flex items-center gap-3 w-full text-left px-4 py-3 rounded-xl hover:bg-blue-100 transition-colors duration-200 group"
                >
                  <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                    <IoPersonOutline className="text-lg text-blue-700" />
                  </div>
                  <span className="font-medium text-blue-800">Profile</span>
                </button>

                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full text-left px-4 py-3 rounded-xl hover:bg-red-50 transition-colors duration-200 group mt-1"
                >
                  <div className="p-2 bg-red-50 rounded-lg group-hover:bg-red-100 transition-colors">
                    <MdOutlineLogout className="text-lg text-red-500" />
                  </div>
                  <span className="font-medium text-red-500">Log out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default E_NavBar;